import { useAuth } from "@assetwatch/auth";
import { useQuery } from "@tanstack/react-query";
import { useFeatureFlags } from "../../components/LaunchDarkly/useFeatureFlags";
import { fetchHubHealthFacilities, fetchHubHealthFacility } from "./api";

/**
 * react-query hooks for the Hub Health page's ML API endpoint.
 *
 * Both take the signed-in user and the feature flags from context and hand them
 * to the fetcher, which is where the access check and the request live.
 */

/**
 * The whole fleet's summaries. This is the page's one call on mount: the page
 * is a worst-first list, so there is nothing to show without it.
 *
 * Fetched once per session — the snapshot changes daily, and every call is a
 * paid Athena scan, so revisiting the page shouldn't re-run it. A reload does.
 * No retries: a rejected caller or an unreadable response fails the same way
 * every attempt, and each attempt is billed.
 */
export function useHubHealthFacilities() {
  const { user } = useAuth();
  const flags = useFeatureFlags();

  return useQuery({
    queryKey: ["hubHealth", "facilities"],
    queryFn: () => fetchHubHealthFacilities({ user, flags }),
    retry: false,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
    meta: { errorMessage: "Could not load hub health facilities." },
  });
}

/**
 * One facility's hubs at the list's snapshot date. Disabled until both are
 * known, so it never fires before the list has said which date to read.
 *
 * A (facility, date) pair never changes, so it's kept for the session: going
 * back to a facility you already opened costs nothing. No retries, as above.
 */
export function useHubHealthFacility(
  facilityId: string | undefined,
  date: string | undefined,
) {
  const { user } = useAuth();
  const flags = useFeatureFlags();

  return useQuery({
    queryKey: ["hubHealth", "facility", facilityId ?? null, date ?? null],
    queryFn: () =>
      fetchHubHealthFacility({
        user,
        flags,
        facilityId: facilityId ?? "",
        date: date ?? "",
      }),
    enabled: Boolean(facilityId && date),
    retry: false,
    staleTime: Number.POSITIVE_INFINITY,
    // Without this the default 5-minute gcTime drops a facility you moved
    // away from, and going back re-runs its paid scan.
    gcTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
    meta: { errorMessage: "Could not load that facility's hubs." },
  });
}
